import {
  ChangeDetectionStrategy,
  Component,
  computed,
  Input,
  inject,
  type OnDestroy,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIconButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatSort } from '@angular/material/sort';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { Currency, Money } from '@principle-theorem/accounting';
import { AppointmentSchedulingFacade } from '@principle-theorem/ng-appointment/store';
import {
  FormattedEventFieldComponent,
  CurrentBrandScope,
  OrganisationService,
} from '@principle-theorem/ng-principle-shared';
import {
  confirmationDialogData,
  ConfirmDialogComponent,
  DialogPresets,
  extendSortingDataAccessor,
  type IConfirmationDialogInput,
  MomentPipe,
  NgCommonModule,
  ObservableDataTable,
  timestampSortingAccessor,
  TrackByFunctions,
} from '@principle-theorem/ng-shared';
import { DepositLifecycle, Event } from '@principle-theorem/principle-core';
import { AppointmentPermissions } from '@principle-theorem/principle-core/features';
import {
  DepositRequestDeliveryChannel,
  DepositStatus,
  DepositUnresolvedReason,
  type IAppointmentRequest,
  type ICreateDepositRequestParams,
  type ICreateDepositRequestResponse,
  type IPatient,
  type ISupersedeDepositRequestParams,
  type IWaiveDepositRequest,
  ParticipantType,
} from '@principle-theorem/principle-core/interfaces';
import {
  DATE_TIME_WITH_YEAR_FORMAT,
  Firestore,
  getError,
  httpsCallable,
  serialise,
  snapshot,
  toNamedDocument,
  type DocumentReference,
  type SerialisedData,
  type WithRef,
} from '@principle-theorem/shared';
import { compact } from 'lodash';
import { MomentModule } from 'ngx-moment';
import { ReplaySubject, type Observable } from 'rxjs';
import { startWith, switchMap } from 'rxjs/operators';
import { ConfirmPatientDialogComponent } from '../confirm-patient-dialog/confirm-patient-dialog.component';
import { DepositActionsComponent } from '../deposit-actions/deposit-actions.component';
import { type IWaiveDepositDialogResult } from '../../appointment-view-sidebar/appointment-deposit-sidebar/waive-deposit-dialog/waive-deposit-dialog.component';
import { type ISendPaymentLinkParams } from '../deposit/deposit.store';
import {
  DepositAbsenceKind,
  type IDepositAbsence,
} from '../deposit-status-chip/deposit-chip-display';
import { DepositStatusChipComponent } from '../deposit-status-chip/deposit-status-chip.component';

@Component({
  selector: 'pr-appointment-requests-list',
  templateUrl: './appointment-requests-list.component.html',
  styleUrls: ['./appointment-requests-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgCommonModule,
    MatIcon,
    MatIconButton,
    MatProgressBar,
    MatTableModule,
    MatTooltipModule,
    MomentModule,
    MomentPipe,
    FormattedEventFieldComponent,
    DepositActionsComponent,
    DepositStatusChipComponent,
  ],
})
export class AppointmentRequestsListComponent implements OnDestroy {
  readonly depositsEnabled = toSignal(
    inject(CurrentBrandScope).depositsEnabled$,
    {
      initialValue: false,
    }
  );
  readonly displayColumns = computed(() => [
    'appointment-date',
    'submitted',
    'practitioner',
    'patient-name',
    'treatment',
    ...(this.depositsEnabled() ? ['deposit'] : []),
    'actions',
  ]);
  trackByRequest = TrackByFunctions.ref<WithRef<IAppointmentRequest>>();
  dataTable: ObservableDataTable<WithRef<IAppointmentRequest>>;
  appointmentRequests$ = new ReplaySubject<WithRef<IAppointmentRequest>[]>(1);
  readonly dateFormat = DATE_TIME_WITH_YEAR_FORMAT;
  readonly DepositStatus = DepositStatus;
  readonly notRequiredAbsence: IDepositAbsence = {
    kind: DepositAbsenceKind.NotRequired,
  };
  readonly patientRequiresAbsence: IDepositAbsence = {
    kind: DepositAbsenceKind.PatientRequires,
  };
  /**
   * Held as stable references rather than built per change detection, so the
   * chip's signal input does not see a new object on every cycle.
   *
   * Keyed by `string` because the table row reaches the template as `any`
   * through `matCellDef`, which cannot index a `DepositUnresolvedReason`
   * record under strict template checking. The `satisfies` clause keeps the
   * exhaustiveness that annotation would otherwise have given.
   */
  readonly unresolvedAbsences: Readonly<Record<string, IDepositAbsence>> = {
    [DepositUnresolvedReason.MissingTreatmentCost]: {
      kind: DepositAbsenceKind.Unresolved,
      unresolvedReason: DepositUnresolvedReason.MissingTreatmentCost,
    },
    [DepositUnresolvedReason.MissingDepositAmount]: {
      kind: DepositAbsenceKind.Unresolved,
      unresolvedReason: DepositUnresolvedReason.MissingDepositAmount,
    },
  } satisfies Record<DepositUnresolvedReason, IDepositAbsence>;
  sendingPaymentLinkRequestId?: string;
  sendingPaymentLinkChannel?: DepositRequestDeliveryChannel;
  flaggedPatientRequestIds$: Observable<Set<string>>;
  canOverrideDeposit$: Observable<boolean>;

  @ViewChild(MatSort)
  set tableSort(sort: MatSort) {
    this.dataTable.dataSource.sort = sort;
  }

  @Input()
  set appointmentRequests(appointmentRequests: WithRef<IAppointmentRequest>[]) {
    if (appointmentRequests) {
      this.appointmentRequests$.next(appointmentRequests);
    }
  }

  constructor(
    private _router: Router,
    private _route: ActivatedRoute,
    private _schedulingFacade: AppointmentSchedulingFacade,
    private _dialog: MatDialog,
    private _organisation: OrganisationService,
    private _snackBar: MatSnackBar
  ) {
    this.canOverrideDeposit$ = this._organisation.userHasPermission$(
      AppointmentPermissions.DepositOverride
    );
    this.flaggedPatientRequestIds$ = this.appointmentRequests$.pipe(
      switchMap((requests) => this._resolveFlaggedPatients(requests)),
      startWith(new Set<string>())
    );
    this.dataTable = new ObservableDataTable(this.appointmentRequests$);
    this.dataTable.dataSource.sortingDataAccessor = extendSortingDataAccessor(
      (data, sortHeaderId) => this._sortingDataAccessor(data, sortHeaderId)
    );
  }

  hasFlaggedPatient(
    flaggedRequestIds: Set<string> | undefined,
    requestId: string
  ): boolean {
    return flaggedRequestIds?.has(requestId) === true;
  }

  ngOnDestroy(): void {
    this.dataTable.ngOnDestroy();
  }

  async cancelAppointmentRequest(
    appointment: WithRef<IAppointmentRequest>
  ): Promise<void> {
    const depositMetadata = this.depositsEnabled()
      ? appointment.deposit?.metadata
      : undefined;
    const hasPaidDeposit = depositMetadata?.status === DepositStatus.Paid;
    const hasAuthorisedDeposit =
      depositMetadata?.status === DepositStatus.Authorised;
    const depositAmount = depositMetadata?.amountCents ?? 0;
    const formattedAmount = Money.formatPlainCents(
      depositAmount,
      depositMetadata?.currency ?? Currency.AUD
    );

    const prompt = hasPaidDeposit
      ? `This booking has a paid deposit of ${formattedAmount}. Rejecting will automatically refund the deposit. Proceed?`
      : hasAuthorisedDeposit
        ? `This booking has an authorised deposit hold of ${formattedAmount}. Rejecting will release the card hold. Proceed?`
        : 'Are you sure you want to delete this request?';

    const data = confirmationDialogData({
      title: 'Delete Appointment Request',
      prompt,
      submitLabel: 'Delete',
      submitColor: 'warn',
    });
    const confirmed = await this._dialog
      .open<ConfirmDialogComponent, IConfirmationDialogInput, boolean>(
        ConfirmDialogComponent,
        DialogPresets.small({ data })
      )
      .afterClosed()
      .toPromise();
    if (!confirmed) {
      return;
    }
    this._schedulingFacade.cancelAppointmentRequest(appointment);
  }

  async confirmAppointmentRequest(
    appointment: WithRef<IAppointmentRequest>
  ): Promise<void> {
    const depositMetadata = this.depositsEnabled()
      ? appointment.deposit?.metadata
      : undefined;
    if (
      depositMetadata &&
      !DepositLifecycle.locksAppointmentDepositRequirement(
        depositMetadata.status
      )
    ) {
      return;
    }
    const confirmPatient = await this._resolveRequestPatient(appointment);

    if (!confirmPatient) {
      return;
    }

    appointment.event = Event.addParticipants(appointment.event, [
      {
        ...toNamedDocument<IPatient>(confirmPatient),
        type: ParticipantType.Patient,
      },
    ]);

    this._schedulingFacade.selectPatient(confirmPatient);
    this._schedulingFacade.setAppointmentRequest(appointment);

    await this._router.navigate(['../../appointments/create'], {
      relativeTo: this._route,
      queryParams: {
        preserveState: true,
      },
      queryParamsHandling: 'merge',
    });
  }

  async sendDepositPaymentLink(
    appointmentRequest: WithRef<IAppointmentRequest>,
    deliveryChannel: DepositRequestDeliveryChannel
  ): Promise<void> {
    const depositMetadata = appointmentRequest.deposit?.metadata;
    if (!depositMetadata || this.sendingPaymentLinkRequestId) {
      return;
    }

    this.sendingPaymentLinkRequestId = appointmentRequest.ref.id;
    this.sendingPaymentLinkChannel = deliveryChannel;
    try {
      const patient = await this._resolveRequestPatient(appointmentRequest);
      if (!patient) {
        return;
      }
      const params: ISendPaymentLinkParams = {
        practiceRef: appointmentRequest.practice.ref,
        appointmentRequestRef: appointmentRequest.ref,
        depositRequestId: appointmentRequest.deposit?.requestRef?.id,
        patientRef: patient.ref,
        treatmentName: appointmentRequest.template.step.name,
        amountCents: depositMetadata.amountCents,
        paymentMode: depositMetadata.paymentMode,
        captureStrategy: depositMetadata.captureStrategy,
        deliveryChannel,
        event: appointmentRequest.event,
      };
      const supersedesTerminalDeposit =
        depositMetadata.status === DepositStatus.Cancelled;
      if (supersedesTerminalDeposit) {
        await this._supersedeDepositRequest(appointmentRequest, params);
        return;
      }

      await snapshot(
        httpsCallable<
          SerialisedData<ICreateDepositRequestParams>,
          SerialisedData<ICreateDepositRequestResponse>
        >('http-stripe-createDepositRequest')(serialise(params))
      );
    } catch (error) {
      this._snackBar.open(
        getError(error, 'Failed to send payment link'),
        undefined,
        { duration: 5000 }
      );
    } finally {
      this.sendingPaymentLinkRequestId = undefined;
      this.sendingPaymentLinkChannel = undefined;
    }
  }

  async waiveDeposit(
    appointmentRequest: WithRef<IAppointmentRequest>,
    result: IWaiveDepositDialogResult
  ): Promise<void> {
    if (!appointmentRequest.deposit?.metadata) {
      return;
    }
    const canOverrideDeposit = await snapshot(this.canOverrideDeposit$);
    if (!canOverrideDeposit) {
      return;
    }
    await snapshot(
      httpsCallable<SerialisedData<IWaiveDepositRequest>, void>(
        'http-stripe-waiveDeposit'
      )(
        serialise({
          appointmentRequestRef: appointmentRequest.ref,
          waiverReason: result.waiverReason,
        })
      )
    );
  }

  private async _supersedeDepositRequest(
    appointmentRequest: WithRef<IAppointmentRequest>,
    params: ICreateDepositRequestParams
  ): Promise<void> {
    const { depositRequestId } = params;
    if (!depositRequestId) {
      throw new Error('Terminal deposit request is missing its request link');
    }
    await snapshot(
      httpsCallable<
        SerialisedData<ISupersedeDepositRequestParams>,
        SerialisedData<ICreateDepositRequestResponse>
      >('http-stripe-supersedeDepositRequest')(
        serialise({
          ...params,
          appointmentRequestRef: appointmentRequest.ref,
          depositRequestId,
        })
      )
    );
  }

  private async _resolveFlaggedPatients(
    requests: WithRef<IAppointmentRequest>[]
  ): Promise<Set<string>> {
    const matched = requests.filter(
      (request) => request.patientRef && !request.deposit
    );
    const flagged = await Promise.all(
      matched.map(async (request) => {
        try {
          const patient = await Firestore.getDoc(
            request.patientRef as DocumentReference<IPatient>
          );
          return patient.depositRequired === true ? request.ref.id : undefined;
        } catch {
          return;
        }
      })
    );
    return new Set(compact(flagged));
  }

  private async _resolveRequestPatient(
    appointmentRequest: WithRef<IAppointmentRequest>
  ): Promise<WithRef<IPatient> | undefined> {
    if (appointmentRequest.patientRef) {
      return Firestore.getDoc(appointmentRequest.patientRef);
    }

    const patient = await this._dialog
      .open(
        ConfirmPatientDialogComponent,
        DialogPresets.almostFullscreen({ data: appointmentRequest.patient })
      )
      .afterClosed()
      .toPromise<WithRef<IPatient>>();
    if (!patient) {
      return;
    }

    await Firestore.patchDoc(appointmentRequest.ref, {
      patientRef: patient.ref,
    });
    return patient;
  }

  private _sortingDataAccessor(
    data: WithRef<IAppointmentRequest>,
    sortHeaderId: string
  ): string | number | undefined {
    switch (sortHeaderId) {
      case 'appointment-date':
        return timestampSortingAccessor(data.event.from);
      case 'submitted':
        return timestampSortingAccessor(data.createdAt);
      case 'practitioner':
        return data.practitioner.name.toLowerCase();
      case 'patient-name':
        return data.patient.primaryContact.name.toLowerCase();
      case 'treatment':
        return data.template.step.name.toLowerCase();
      default:
        return;
    }
  }
}
